import React, { FC, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityIroTypeEnum,
  DoubleMaterialityStakeholderESRSArea,
  DoubleMaterialityStakeholderImpactArea,
} from "@green0meter/api-client";
import type {
  QuestionAnswerRowDto,
  StakeholderPublicInfoDto,
  CompanyPublicInfoDto,
  DoubleMaterialityStakeholderTypeEnum,
} from "@green0meter/api-client";
import axios from "axios";
import DOMPurify from "dompurify";

import { DMSurveyInternal } from "./DMSurveyInternal";
import { DMSurveyIntroduction } from "./DMSurveyIntroduction";
import { FivePointScale } from "./FivePointScale";
import { DMSurveySuccess } from "./DMSurveySuccess";
import { getFullLogoUrl } from "./DMSurveyLogo";

import { doubleMaterialityQuestionAnswersApi } from "@api/services";
import { useESRSLabels } from "@components/main/csrd/DoubleMateriality/components/Stakeholders/components/useESRSLabels";
import {
  ESRS_TYPES,
  ESRS_STRING_TO_ENUM,
} from "@components/main/csrd/DoubleMateriality/components/Stakeholders/components/ESRS.constants";
import { RichTextBox } from "@components/common";
import {
  DM_SUPPORTED_LANGUAGES,
  DM_RANGE_KEYS,
  type DmRangeKey,
} from "@constants/dmRanges";

const SURVEY_COPY_FIELDS = [
  "ratingLabel",
  "additionalComments",
  "submitButtonLabel",
  "submittingLabel",
  "noQuestions",
  "deadlinePassedTitle",
  "deadlinePassedMessage",
] as const;

type SurveyCopyField = (typeof SURVEY_COPY_FIELDS)[number];
type SurveyFormCopy = Partial<Record<SurveyCopyField, string>>;

type SurveyRanges = Record<DmRangeKey, string[]>;
type SurveyFormRanges = Partial<Record<DmRangeKey, string[]>>;

const normalizeRangeValues = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;

  const targetLength = value.length > 3 ? 5 : 3;

  const normalized = Array.from({ length: targetLength }, (_, index) => {
    const entry = value[index];

    if (typeof entry === "string") {
      return entry;
    }

    if (typeof entry === "number") {
      return entry.toString();
    }

    return "";
  });

  return normalized;
};

const getLanguageSurveyRanges = (
  ranges: Record<string, unknown> | null | undefined,
  languageCode: string,
): SurveyFormRanges => {
  if (!ranges || typeof ranges !== "object") {
    return {};
  }

  const languagePayload = (ranges as Record<string, unknown>)[languageCode];

  if (!languagePayload || typeof languagePayload !== "object") {
    return {};
  }

  const result: SurveyFormRanges = {};

  DM_RANGE_KEYS.forEach((key) => {
    const maybeValues = (languagePayload as Record<string, unknown>)[key];
    const normalized = normalizeRangeValues(maybeValues);

    if (normalized) {
      result[key] = normalized;
    }
  });

  return result;
};

const buildDefaultSurveyRanges = (t: TFunction): SurveyRanges => ({
  likelihood: [
    t("dmSurvey.veryUnlikely", { defaultValue: "Very unlikely" }),
    "2",
    "3",
    "4",
    t("dmSurvey.almostCertain", { defaultValue: "Almost certain" }),
  ],
  intensity: [
    t("dmSurvey.minimal", { defaultValue: "Minimal" }),
    "2",
    "3",
    "4",
    t("dmSurvey.absolute", { defaultValue: "Absolute" }),
  ],
  scope: [
    t("dmSurvey.limited", { defaultValue: "Limited" }),
    "2",
    "3",
    "4",
    t("dmSurvey.globalOverall", { defaultValue: "Global/overall" }),
  ],
  remediability: [
    t("dmSurvey.easilyRemediable", {
      defaultValue: "Easily remediable",
    }),
    "2",
    "3",
    "4",
    t("dmSurvey.irremediable", { defaultValue: "Irremediable" }),
  ],
  scale: ["1", "2", "3", "4", "5"],
  timeHorizon: ["1", "3", "5"],
  riskLikelihood: [
    t("dmSurvey.veryUnlikely", { defaultValue: "Very unlikely" }),
    "2",
    "3",
    "4",
    t("dmSurvey.almostCertain", { defaultValue: "Almost certain" }),
  ],
  riskMagnitude: ["1", "2", "3", "4", "5"],
  riskTimeHorizon: ["1", "3", "5"],
  opportunityLikelihood: [
    t("dmSurvey.veryUnlikely", { defaultValue: "Very unlikely" }),
    "2",
    "3",
    "4",
    t("dmSurvey.almostCertain", { defaultValue: "Almost certain" }),
  ],
  opportunityMagnitude: ["1", "2", "3", "4", "5"],
  opportunityTimeHorizon: ["1", "3", "5"],
});

const getLanguageFormCopy = (
  formCopy: Record<string, unknown> | null | undefined,
  languageCode: string,
): SurveyFormCopy => {
  if (!formCopy || typeof formCopy !== "object") {
    return {};
  }

  const languageEntry = formCopy[languageCode];

  if (!languageEntry || typeof languageEntry !== "object") {
    return {};
  }

  const result: SurveyFormCopy = {};

  SURVEY_COPY_FIELDS.forEach((field) => {
    const maybeValue = (languageEntry as Record<string, unknown>)[field];

    if (typeof maybeValue === "string") {
      result[field] = maybeValue;
    }
  });

  return result;
};

interface QuestionData {
  sustainabilityMatter?: string;
  questions?: Record<string, string> | string;
  descriptions?: Record<string, string> | string;
  assessment?: {
    assessmentId?: string;
    sustainabilityMatter?: string;
    topic?: DoubleMaterialityESRSAreaEnum;
  };
  iro: DoubleMaterialityIroTypeEnum | null;
  stakeholderType: DoubleMaterialityStakeholderTypeEnum | null;
  activateTextField?: boolean;
  textFieldLabels?: Record<string, string> | null;
}

interface QuestionWithAnswer extends QuestionAnswerRowDto {
  year: number;
  id: string;
  question?: QuestionData;
}

const DMSurveyPage: FC = () => {
  const { stakeholderId } = useParams<{ stakeholderId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const { getEsrsLabelFromEnum } = useESRSLabels();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [answers, setAnswers] = useState<
    Record<
      string,
      {
        value: number;
        textAnswer: string;
      }
    >
  >({});
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [stakeholderInfo, setStakeholderInfo] =
    useState<StakeholderPublicInfoDto | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyPublicInfoDto | null>(null);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stakeholderComment, setStakeholderComment] = useState<string>("");
  const autoSaveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const commentAutoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const companyLogoUrl = useMemo(
    () => getFullLogoUrl(companyInfo?.logo, stakeholderInfo?.surveyCompanyId),
    [companyInfo?.logo, stakeholderInfo?.surveyCompanyId],
  );

  const stakeholderLanguageCode = useMemo(() => {
    if (stakeholderInfo?.language == null) {
      return "en";
    }

    return (
      DM_SUPPORTED_LANGUAGES.find(({ id }) => id === stakeholderInfo.language)
        ?.code ?? "en"
    );
  }, [stakeholderInfo?.language]);

  useEffect(() => {
    if (stakeholderLanguageCode && i18n.language !== stakeholderLanguageCode) {
      void i18n.changeLanguage(stakeholderLanguageCode);
    }
  }, [stakeholderLanguageCode, i18n]);

  const defaultSurveyCopy = useMemo(
    () =>
      SURVEY_COPY_FIELDS.reduce(
        (acc, field) => {
          const key = `dmSurvey.${field}`;
          const fallbacks: Record<SurveyCopyField, string> = {
            ratingLabel:
              "Assess the sustainability matter (1 = very low, 5 = very high):",
            additionalComments:
              "Is there anything else you would like to share about this topic? (optional)",
            submitButtonLabel: "Submit",
            submittingLabel: "Submitting...",
            noQuestions:
              "No questions have been assigned to you yet. Please check back later or contact the administrator.",
            deadlinePassedTitle: "Survey Deadline Has Passed",
            deadlinePassedMessage:
              "The deadline for this survey was {{deadline}}. Unfortunately, the survey is now closed and responses can no longer be submitted.",
          };

          acc[field] = t(key, { defaultValue: fallbacks[field] });

          return acc;
        },
        {} as Record<SurveyCopyField, string>,
      ),
    [t],
  );

  const defaultSurveyRanges = useMemo(() => buildDefaultSurveyRanges(t), [t]);

  const surveyCopy = useMemo(() => {
    const result = {} as Record<SurveyCopyField, string>;
    const languageCopy = getLanguageFormCopy(
      stakeholderInfo?.formCopy as Record<string, unknown> | null | undefined,
      stakeholderLanguageCode,
    );

    SURVEY_COPY_FIELDS.forEach((field) => {
      const customValue =
        typeof languageCopy[field] === "string" ? languageCopy[field]?.trim() : "";

      result[field] = customValue ? customValue : defaultSurveyCopy[field];
    });

    return result;
  }, [stakeholderInfo?.formCopy, stakeholderLanguageCode, defaultSurveyCopy]);

  const surveyRanges = useMemo(() => {
    const base = DM_RANGE_KEYS.reduce((acc, key) => {
      acc[key] = [...defaultSurveyRanges[key]];

      return acc;
    }, {} as SurveyRanges);

    const languageRanges = getLanguageSurveyRanges(
      stakeholderInfo?.ranges as Record<string, unknown> | null | undefined,
      stakeholderLanguageCode,
    );

    DM_RANGE_KEYS.forEach((key) => {
      const customValues = languageRanges[key];

      if (!customValues) {
        return;
      }

      base[key] = base[key].map((defaultValue, index) => {
        const candidate = (customValues[index] ?? "").trim();

        return candidate.length > 0 ? candidate : defaultValue;
      });
    });

    return base;
  }, [defaultSurveyRanges, stakeholderInfo?.ranges, stakeholderLanguageCode]);

  const visibleQuestions = useMemo(() => {
    if (!stakeholderInfo || !questions.length) return [];

    return questions.filter((question) => {
      // Check if question topic matches assigned ESRS areas
      const questionTopic = question.question?.assessment?.topic;
      const matchesESRSArea =
        stakeholderInfo.esrsAreas &&
        stakeholderInfo.esrsAreas.length > 0 &&
        questionTopic !== undefined
          ? stakeholderInfo.esrsAreas.some(
              (area: DoubleMaterialityStakeholderESRSArea) => ESRS_STRING_TO_ENUM[ESRS_TYPES[area]] === questionTopic,
            )
          : true;

      if (!matchesESRSArea) {
        return false;
      }

      // Check if stakeholder can assess this question's IRO type
      const questionIroType = question.question?.iro;

      if (questionIroType !== undefined && questionIroType !== null) {
        const iroTypeMap: Record<DoubleMaterialityIroTypeEnum, string> = {
          [DoubleMaterialityIroTypeEnum.Impact]: "impact",
          [DoubleMaterialityIroTypeEnum.Risk]: "risk",
          [DoubleMaterialityIroTypeEnum.Opportunity]: "opportunity",
        };

        const iroValue = iroTypeMap[questionIroType];
        const canAssessThisIroType =
          !stakeholderInfo.impactAreas ||
          stakeholderInfo.impactAreas.length === 0 ||
          stakeholderInfo.impactAreas.some(
            (area: DoubleMaterialityStakeholderImpactArea) => area.toLowerCase() === iroValue,
          );

        if (!canAssessThisIroType) {
          return false;
        }
      }

      return true;
    });
  }, [questions, stakeholderInfo]);

  const applyDeadlinePlaceholder = useCallback(
    (template: string, formattedDeadline: string) =>
      template.replace(/\{\{\s*deadline\s*\}\}/gi, formattedDeadline),
    [],
  );

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!stakeholderId) {
        setError(t("dmSurvey.invalidLink", "Invalid survey link"));
        setLoading(false);

        return;
      }

      try {
        setLoading(true);

        // Fetch stakeholder info (deadline and name)
        const stakeholderResponse = await axios.get<StakeholderPublicInfoDto>(
          `/api/dm/stakeholders/public/${stakeholderId}`,
        );
        const stakeholderData = stakeholderResponse.data;

        setStakeholderInfo(stakeholderData);
        setStakeholderComment(stakeholderData.surveyComment || "");

        // Check if deadline has passed
        if (stakeholderData.deadline) {
          const deadline = new Date(stakeholderData.deadline);
          const now = new Date();

          setDeadlinePassed(deadline < now);
        }

        // Fetch questions
        const response =
          await doubleMaterialityQuestionAnswersApi.doubleMaterialityQuestionAnswersControllerFindAllForStakeholder(
            stakeholderId,
          );

        const questionsData = response.data as QuestionWithAnswer[];

        // Check if no questions found
        if (!questionsData || questionsData.length === 0) {
          setNotFound(true);
          setLoading(false);

          return;
        }

        setQuestions(questionsData);

        // Initialize answers from existing data
        const initialAnswers: Record<string, { value: number; textAnswer: string }> =
          {};

        questionsData.forEach((q) => {
          if (q.questionId) {
            const isInternal = stakeholderData.type === "internal";
            const answerValue = isInternal ? q.scope : q.externalAnswer;

            if (answerValue !== null && answerValue !== undefined) {
              initialAnswers[q.questionId] = {
                value: answerValue,
                textAnswer: q.textAnswer ?? "",
              };
            }
          }
        });
        setAnswers(initialAnswers);
        setError(null);
      } catch (err) {
        // Check if it's a 404 error
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
        } else {
          // Failed to fetch data
          setError(
            t(
              "dmSurvey.loadError",
              "Failed to load survey questions. Please check your link and try again.",
            ),
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [stakeholderId, t]);

  useEffect(() => {
    const fetchCompanyInfo = async (): Promise<void> => {
      if (!stakeholderInfo?.surveyCompanyId) return;

      try {
        const response = await axios.get<CompanyPublicInfoDto>(
          `/api/companies/public/${stakeholderInfo.surveyCompanyId}`,
        );

        setCompanyInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch company info:", error);
      }
    };

    fetchCompanyInfo();
  }, [stakeholderInfo?.surveyCompanyId]);

  // Auto-save answer with debouncing
  const autoSaveAnswer = useCallback(
    async (
      questionId: string,
      value: number,
      textAnswer: string,
      year: number,
    ): Promise<void> => {
      if (!stakeholderId || !stakeholderInfo) return;

      try {
        const isInternal = stakeholderInfo.type === "internal";

        await doubleMaterialityQuestionAnswersApi.doubleMaterialityQuestionAnswersControllerUpsert(
          {
            questionId,
            stakeholderId,
            year,
            ...(isInternal ? { scope: value } : { externalAnswer: value }),
            textAnswer: textAnswer || undefined,
          },
        );
      } catch {
        // Silently fail on auto-save
      }
    },
    [stakeholderId, stakeholderInfo],
  );

  const handleAnswerChange = (
    questionId: string,
    value: number,
    year: number,
  ): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        value,
      },
    }));

    // Debounced auto-save
    if (autoSaveTimeoutRef.current[questionId]) {
      clearTimeout(autoSaveTimeoutRef.current[questionId]);
    }

    autoSaveTimeoutRef.current[questionId] = setTimeout(() => {
      const currentAnswer = answers[questionId] || { value: 1, textAnswer: "" };

      autoSaveAnswer(questionId, value, currentAnswer.textAnswer, year);
    }, 1000);
  };

  const handleTextAnswerChange = (
    questionId: string,
    textAnswer: string,
    year: number,
  ): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textAnswer,
      },
    }));

    // Debounced auto-save
    if (autoSaveTimeoutRef.current[questionId]) {
      clearTimeout(autoSaveTimeoutRef.current[questionId]);
    }

    autoSaveTimeoutRef.current[questionId] = setTimeout(() => {
      const currentAnswer = answers[questionId] || { value: 1, textAnswer: "" };

      autoSaveAnswer(questionId, currentAnswer.value, textAnswer, year);
    }, 1000);
  };

  const handleStakeholderCommentChange = (comment: string): void => {
    setStakeholderComment(comment);

    if (commentAutoSaveTimeoutRef.current) {
      clearTimeout(commentAutoSaveTimeoutRef.current);
    }

    commentAutoSaveTimeoutRef.current = setTimeout(async () => {
      if (!stakeholderId) return;

      await axios.put(`/api/dm/stakeholders/public/${stakeholderId}/comment`, {
        comment,
      });
    }, 1000);
  };

  const handleSurveySubmit = async (): Promise<void> => {
    if (!stakeholderId || !stakeholderInfo) return;

    try {
      setSubmitting(true);

      // Clear any pending auto-saves
      Object.values(autoSaveTimeoutRef.current).forEach((timeout) =>
        clearTimeout(timeout),
      );
      autoSaveTimeoutRef.current = {};

      const isInternal = stakeholderInfo.type === "internal";

      // Save all answers immediately
      await Promise.all(
        questions.map((q) => {
          if (q.questionId) {
            const answer = answers[q.questionId];

            if (answer) {
              return doubleMaterialityQuestionAnswersApi.doubleMaterialityQuestionAnswersControllerUpsert(
                {
                  questionId: q.questionId,
                  stakeholderId,
                  year: q.year,
                  ...(isInternal
                    ? { scope: answer.value }
                    : { externalAnswer: answer.value }),
                  textAnswer: answer.textAnswer || undefined,
                },
              );
            }
          }

          return Promise.resolve();
        }),
      );

      // Mark survey as submitted
      await axios.post(`/api/dm/stakeholders/public/${stakeholderId}/submit`);

      // Refetch stakeholder info to get updated submittedAt
      const stakeholderResponse = await axios.get<StakeholderPublicInfoDto>(
        `/api/dm/stakeholders/public/${stakeholderId}`,
      );

      setStakeholderInfo(stakeholderResponse.data);

      enqueueSnackbar(
        t("dmSurvey.submitSuccess", "Survey submitted successfully!"),
        {
          variant: "success",
        },
      );
    } catch {
      enqueueSnackbar(
        t("dmSurvey.submitError", "Failed to submit survey. Please try again."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h1"
              fontSize="6rem"
              fontWeight={700}
              color="text.secondary"
            >
              {"404"}
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {t("dmSurvey.notFound", "Survey Not Found")}
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {t(
                "dmSurvey.notFoundMessage",
                "The survey you are looking for does not exist or has no questions assigned. Please check the link and try again.",
              )}
            </Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (stakeholderInfo?.submittedAt) {
    return <DMSurveySuccess submittedAt={stakeholderInfo.submittedAt} />;
  }

  // If this is an internal stakeholder, use the internal survey component
  if (stakeholderInfo?.type === "internal") {
    const handleSubmitSuccess = async (): Promise<void> => {
      // Refetch stakeholder info to get updated submittedAt
      const stakeholderResponse = await axios.get<StakeholderPublicInfoDto>(
        `/api/dm/stakeholders/public/${stakeholderId}`,
      );

      setStakeholderInfo(stakeholderResponse.data);
    };

    // Filter questions by assigned ESRS topics and impact areas
    const filteredQuestions = visibleQuestions;

    if (filteredQuestions.length === 0 && questions.length > 0) {
      return (
        <Box sx={{ bgcolor: "rgba(248, 248, 248, 1)", minHeight: "100vh", py: 4 }}>
          <Container maxWidth="md">
            <Paper elevation={0} sx={{ p: 3, bgcolor: "background.paper" }}>
              <Typography variant="h6" color="text.secondary" textAlign="center">
                {t(
                  "dmSurvey.noQuestionsAssigned",
                  "No relevant questions have been assigned to you yet. Please contact your greenometer administrator.",
                )}
              </Typography>
            </Paper>
          </Container>
        </Box>
      );
    }

    return (
      <DMSurveyInternal
        stakeholderId={stakeholderId!}
        stakeholderInfo={stakeholderInfo}
        questions={filteredQuestions}
        onSubmitSuccess={handleSubmitSuccess}
        surveyRanges={surveyRanges}
      />
    );
  }

  // Filter questions by assigned ESRS topics and impact areas for external stakeholders
  const filteredExternalQuestions = visibleQuestions;

  // Group questions by ESRS topic
  const groupedQuestions = filteredExternalQuestions.reduce(
    (acc, q) => {
      const topic = q.question?.assessment?.topic;
      const esrsLabel =
        topic !== undefined ? getEsrsLabelFromEnum(topic) : "Uncategorized";

      if (!acc[esrsLabel]) {
        acc[esrsLabel] = [];
      }
      acc[esrsLabel].push(q);

      return acc;
    },
    {} as Record<string, QuestionWithAnswer[]>,
  );

  return (
    <Box sx={{ bgcolor: "rgba(248, 248, 248, 1)", minHeight: "100vh", height: "100vh", overflow: "auto", py: 4 }}>
      <Container maxWidth="md">
        <Stack spacing={4}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, bgcolor: "background.paper" }}>
            <DMSurveyIntroduction
              companyName={companyInfo?.name}
              companyLogo={companyLogoUrl}
              deadline={stakeholderInfo?.deadline}
            />
          </Paper>

          {/* Deadline Warning */}
          {deadlinePassed && stakeholderInfo?.deadline && (
            <Alert severity="error">
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {surveyCopy.deadlinePassedTitle}
              </Typography>
              <Typography variant="body2">
                {applyDeadlinePlaceholder(
                  surveyCopy.deadlinePassedMessage,
                  new Date(stakeholderInfo.deadline).toLocaleDateString(),
                )}
              </Typography>
            </Alert>
          )}

          {/* Questions grouped by ESRS topic */}
          {!deadlinePassed &&
            Object.entries(groupedQuestions).map(([esrsLabel, topicQuestions]) => {
              return (
                <Stack key={esrsLabel} spacing={3}>
                  {topicQuestions.map((q) => {
                    const questionId = q.questionId;

                    if (!questionId) return null;

                    const currentAnswer = answers[questionId];

                    const questionText =
                      typeof q.question?.questions === "object"
                        ? (q.question.questions as Record<string, string>)[
                            stakeholderLanguageCode
                          ] ||
                          (q.question.questions as Record<string, string>)?.en ||
                          (q.question.questions as Record<string, string>)?.cs ||
                          ""
                        : q.question?.questions || "";

                    const descriptionText =
                      typeof q.question?.descriptions === "object"
                        ? (q.question.descriptions as Record<string, string>)[
                            stakeholderLanguageCode
                          ] ||
                          (q.question.descriptions as Record<string, string>)?.en ||
                          (q.question.descriptions as Record<string, string>)?.cs ||
                          ""
                        : q.question?.descriptions || "";

                    const textFieldLabels = q.question?.textFieldLabels;
                    let textFieldLabel = null;

                    if (
                      textFieldLabels &&
                      typeof textFieldLabels === "object" &&
                      Object.keys(textFieldLabels).length > 0
                    ) {
                      textFieldLabel =
                        (textFieldLabels as Record<string, string>)[
                          stakeholderLanguageCode
                        ] ||
                        (textFieldLabels as Record<string, string>)["en"] ||
                        Object.values(textFieldLabels)[0];
                    }

                    return (
                      <Card key={q.id} elevation={1} sx={{ bgcolor: "#f5f5f5" }}>
                        <CardContent>
                          <Stack spacing={3}>
                            {/* Question Header */}
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="flex-start"
                            >
                              <Box flex={1}>
                                <Typography
                                  variant="h6"
                                  component="h2"
                                  mb={1}
                                  fontWeight="bold"
                                >
                                  {esrsLabel}
                                  {" - "}
                                  {q.question?.assessment?.sustainabilityMatter ||
                                    t("dmSurvey.generalTopic", "General Topic")}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                  component="div"
                                >
                                  {q.question?.assessment?.assessmentId &&
                                    `[${q.question.assessment.assessmentId}] `}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: questionText,
                                    }}
                                  />
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="div"
                                  dangerouslySetInnerHTML={{
                                    __html: descriptionText,
                                  }}
                                />
                              </Box>
                            </Stack>

                            {/* Rating Scale */}
                            <Box
                              sx={{
                                bgcolor: "rgba(0, 0, 0, 0.05)",
                                p: 2,
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                fontWeight="normal"
                              >
                                {surveyCopy.ratingLabel}
                              </Typography>
                              <FivePointScale
                                value={currentAnswer?.value?.toString() || ""}
                                onChange={(value) =>
                                  handleAnswerChange(
                                    questionId,
                                    parseInt(value, 10),
                                    q.year,
                                  )
                                }
                                labels={surveyRanges.scale}
                              />
                            </Box>
                            {/* Text Answer - only show when activateTextField is enabled */}
                            {q.question?.activateTextField && (
                              <Box
                                sx={{
                                  bgcolor: "rgba(0, 0, 0, 0.05)",
                                  p: 2,
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  fontWeight="normal"
                                  sx={{ mb: 2 }}
                                  component="div"
                                >
                                  {textFieldLabel ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(textFieldLabel),
                                      }}
                                    />
                                  ) : (
                                    surveyCopy.additionalComments
                                  )}
                                </Typography>
                                <RichTextBox
                                  value={currentAnswer?.textAnswer || ""}
                                  onChange={(value) =>
                                    handleTextAnswerChange(questionId, value, q.year)
                                  }
                                />
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              );
            })}

          {/* Stakeholder general comment box */}
          {!deadlinePassed && filteredExternalQuestions.length > 0 && (
            <Box
              sx={{
                bgcolor: "rgba(0, 0, 0, 0.05)",
                p: 2,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="normal"
                sx={{ mb: 2 }}
              >
                {surveyCopy.additionalComments}
              </Typography>
              <RichTextBox
                value={stakeholderComment}
                onChange={handleStakeholderCommentChange}
              />
            </Box>
          )}

          {/* Submit Survey Button */}
          {!deadlinePassed && filteredExternalQuestions.length > 0 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSurveySubmit}
                disabled={submitting}
                sx={{ minWidth: "100%" }}
              >
                {submitting
                  ? surveyCopy.submittingLabel
                  : surveyCopy.submitButtonLabel}
              </Button>
            </Box>
          )}

          {/* No Questions Message */}
          {!deadlinePassed && filteredExternalQuestions.length === 0 && (
            <Alert severity="info">{surveyCopy.noQuestions}</Alert>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default DMSurveyPage;
